/**
 * Enquiries on `quoflow.quoflow.quoflow.api.enquiry.*` — Task 7 of the migration. Live shapes
 * verified against rfqdev.etoserp.com, 2026-08-04 (see the task dispatch); follows the pattern
 * `useItems.ts`/`useMail.ts` set: a `Wire*` interface next to each hook, a pure exported `toX`
 * converter, no shared mapper file (except `mappers/status.ts`, which this task keeps — see its
 * own header).
 *
 * `useRfqs` moved onto `rfq.get_rfqs` in Task 8 — it replaces the `RFQ`-doctype `get_list`/
 * `get_document` pair it used to run (see `mappers/rfq.ts`'s header for why that was one document
 * per supplier, no fan-out; that mapper is now unused here but left in place — Task 12 deletes the
 * old mappers, not this one). `useEnquirySources`, which used to sit here calling the mock-only
 * `/api/v1/enquiries/:id/sources`, is DELETED — see `features/rfq/SourcePanel.tsx`'s header for
 * its live replacement: `enquiry.mail_id` (added to `WireEnquiryDetail`/`toEnquiryHeader` below)
 * plus `mail.get_email`, both real methods already used elsewhere in this codebase.
 *
 * ---- age_days replaces the status_changed_at approximation ----
 *
 * The previous version of this file built `EnquirySummary.status_changed_at`/`Enquiry.
 * status_changed_at` from `Opportunity.modified` — a Frappe implicit column that updates on ANY
 * edit, not just a status change, so the "Awaiting N days" badge and the 30-day auto-archive were
 * both reading a proxy, not the real thing (see the git history of this file, and
 * `mappers/opportunity.ts`'s `approxAgeDays`, which keeps that approximation alive for the one
 * remaining old-transport caller). `get_enquiries`/`get_enquiry` return a real, server-computed
 * `age_days` instead — this file just passes it through. That whole approximation, and the
 * comment block explaining why it was unreliable, is DELETED here, not carried forward.
 *
 * `awaiting_gt_days` (dropped from the previous version as unimplementable — see that same git
 * history) is restored: filtered client-side against `age_days`, which is now honest to filter on
 * because the server itself computed it, not because a client-side day-count is suddenly a
 * trustworthy proxy for a security- or business-relevant cutoff. It runs AFTER the server's own
 * `limit`/`offset` paging, so — same caveat any client-side post-filter over a paged list carries
 * — it filters the page actually returned, not the full unpaged set; every call site today asks
 * for a large `limit` (the whole board), so this is a documented limitation, not a live bug.
 *
 * ---- known gaps in the wire shape (not fabricated, not guessed) ----
 *
 * `get_enquiries`/`get_enquiry` carry NO creation timestamp at all (no `creation`, no
 * `created_at`), and `get_enquiries`' row carries no `sales_person`. Both `EnquirySummary`/
 * `Enquiry` still declare these fields (this task's type change is scoped to `status_changed_at`→
 * `age_days` and `value`←`total` only), so they map to `''` — an honest blank, not a fabricated
 * date/name. The practical effect: the "N old" age badge (`RequirementCard`, `ContextBar`), which
 * reads `created_at`, shows "today" for every card until the server adds the field. Flagged in
 * task-7-report.md for the backend follow-up list; the mock does not paper over this either (see
 * `mock-server/bosun/handlers/enquiry.ts`'s own header) — same reasoning `useMail.ts` uses for
 * reproducing defect B4 rather than hiding it locally.
 *
 * `get_enquiry` also carries none of `EnquiryHeader`'s secondary fields (`subject`, `sender`,
 * `company`, `company_series`, `department`, `sales_person`, `use_email_body`, `vessel`,
 * `delivery_location`, `delivery_port`, `type`, `date`) — only `customer`, `enquiry_type` and
 * `purchaser` overlap it. `EnquiryHeaderCard.tsx` shows the rest read-only/blank with an inline
 * explanation rather than silently dropping the fields from the type; see that file's header.
 *
 * ---- CORRECTION 2026-08-04: the paragraph above was modelled on the collection's abbreviated
 * example response, not a live call, and it is mostly wrong ----
 *
 * A live `enquiry.get_enquiry?name=` against `rfqdev.etoserp.com` returns `sales_person`,
 * `vessel`, `delivery_location`, `port_of_delivery` (→ `delivery_port`), `use_email_body`,
 * `transaction_date` (→ `date`) and `customer` — SEVEN of the eleven fields the paragraph above
 * claimed had no wire source at all. Only `subject`, `sender`, `company`, `company_series` and
 * `department` are genuinely absent (verified: not in the field list below). `type` ('Auto' vs
 * 'Manual') also has no field of its own, but IS derivable from `source` ('mail' | 'manual'),
 * which the live response does carry — see `toEnquiryHeader` below.
 *
 * The live response's full field list (2026-08-04): `name, owner, creation, modified,
 * modified_by, docstatus, idx, naming_series, status, source, enquiry_type, transaction_date,
 * ams_user_record_no, status_changed_at, total, customer, customer_name,
 * customer_reference_no, customer_reference_date, sales_person, purchaser,
 * assigned_to_purchaser_on, priority, vessel, port_of_delivery, delivery_location, etd,
 * price_list, warehouse, number_of_days, mail_id, with_items, use_email_body,
 * remarks_to_supplier, enquiry_remarks, customer_details, doctype, items[], file_mapping[],
 * age_days, ref, rfqs[], rfq_count`. `WireEnquiryDetail` below models the subset this file's
 * mappers actually consume, not every column — an unmodelled one (e.g. `etd`, `warehouse`,
 * `mail_id`) still survives a write untouched (see `useEnquiryMutations.ts`'s `toWireItemWrite`
 * for the equivalent item-level guarantee; the header write path below sends a scalar PATCH-style
 * body, never a wholesale object, so it can't clobber a field it doesn't name either).
 *
 * Two more corrections this same measurement forced:
 *
 * - `status_changed_at` and `assigned_to_purchaser_on` DO exist on the wire — the belief that
 *   there was no status timestamp at all (which is why `age_days` was built server-side rather
 *   than trusted to exist) was itself based on the same abbreviated example. `age_days` is left
 *   exactly as Task 7 built it — it's server-computed and correct — this just stops the file from
 *   implying no timestamp exists; nothing here switches back to a client-computed approximation.
 * - `ref` and `ams_user_record_no` ARE both present on this wire (see `toEnquiry` below) — the
 *   "get_enquiry carries no ams_user_record_no/ref" line that used to sit on `toEnquiry` was the
 *   same error, now fixed the same way `toEnquirySummary` already handles it.
 *
 * Not re-verified by this pass (left exactly as Task 7 found them, still honestly blank): the
 * `creation`/`created_at` gap on `EnquirySummary`/`Enquiry` and `get_enquiries`' missing
 * `sales_person` — `get_enquiry`'s response DOES carry `creation`, measured above, but wiring it
 * into `created_at` touches `RequirementCard`/`ContextBar`/`get_enquiries`' row shape, none of
 * which this pass touched or re-probed. Worth a follow-up, not asserted fixed here.
 */

import { useQuery } from '@tanstack/react-query'
import { callGet } from './bosun'
import { bool01, money, num, str, trimmed, type WireList } from './wire'
import { toBoardStatus } from './mappers/status'
import { getBaseCurrency } from './baseCurrency'
import { qk, type EnquiryQuery } from './keys'
import { POLL_MS } from './polling'
import type { Enquiry, EnquiryHeader, EnquiryItem, EnquiryType, LineRoute, EnquirySummary, Rfq, RfqStatus } from '../types'

/* ============================================================================================
 * shared: enquiry_type validation (server currently only ever sends Provision | Technical, but
 * EnquiryType also carries Bosun's Commercial | Bonded Stores — same guard `mappers/opportunity.
 * ts` used, kept local here since this file no longer shares that module).
 * ============================================================================================ */

function toEnquiryType(value: unknown): EnquiryType {
  return value === 'Technical' || value === 'Provision' || value === 'Commercial' || value === 'Bonded Stores'
    ? value
    : 'Commercial'
}

/* ============================================================================================
 * enquiry.get_enquiries → useEnquiries
 * ============================================================================================ */

export interface WireEnquirySummary {
  name: string
  ams_user_record_no?: string | null
  customer: string
  customer_name?: string
  status: string
  enquiry_type: string
  age_days: number
  total: number
  purchaser: string
  ref?: string | null
  /**
   * ---- CORRECTION 2026-08-09 ----
   *
   * The module header said this row carries no date and no `sales_person`. It carries both. The
   * full live key set, measured today:
   *
   *     age_days, ams_user_record_no, customer, customer_name, delivery_location, enquiry_type,
   *     mail_id, modified, name, priority, purchaser, ref, sales_person, status,
   *     status_changed_at, total, transaction_date, vessel
   *
   * The consequence of not modelling `transaction_date` was visible on every card: `created_at`
   * mapped to `''`, `formatAge('')` returns `'today'` (its `NaN` guard), so the board said
   * "today old" for an enquiry raised three weeks ago — an age badge that was not merely missing
   * but confidently wrong.
   *
   * `creation`/`created_at` really are absent HERE (they are on `get_enquiry`), so `created_at`
   * stays blank and `date` carries the real answer.
   */
  transaction_date?: string | null
  sales_person?: string | null
  /** The vessel code — on the list row (measured 2026-09-14, `get_enquiries` keys). */
  vessel?: string | null
  /**
   * The `Communication` this enquiry was created from — listed in the measured key set above but
   * never modelled until now. Read so the mail screen can say which mails have already been turned
   * into an enquiry (`lib/mailProcessed.ts`); 85 of 99 rows carry one.
   */
  mail_id?: string | null
}

/** `baseCurrency` labels `total`, which arrives as a bare number (contract: money on the wire is
 * a bare number, currency lives on the parent — here that's the company's own base currency). */
export function toEnquirySummary(w: WireEnquirySummary, baseCurrency: string = getBaseCurrency()): EnquirySummary {
  const ref = str(w.ref) || str(w.ams_user_record_no) || w.name
  return {
    id: w.name,
    ref,
    status: toBoardStatus(w.status),
    // Kept alongside the folded value so the board can tell a real `Open` from a status this
    // client has never heard of — the fold is silent, see `isKnownBoardStatus`.
    status_raw: w.status,
    created_at: '', // genuinely absent from the LIST row — see `WireEnquirySummary`'s correction
    date: str(w.transaction_date),
    age_days: num(w.age_days),
    // IDENTITY, never the display label (task 12 fallback-pattern sweep — this used to prefer
    // `customer_name`, exactly the conflation `Quotation.customer`'s own comment warns against).
    customer: w.customer,
    customer_name: str(w.customer_name) || w.customer,
    sales_person: str(w.sales_person), // IS on this row — see `WireEnquirySummary`'s correction
    // On the LIST row too, not only the detail. Lets the mail screen say which mails are done.
    mail_id: w.mail_id ? str(w.mail_id) : null,
    purchaser: str(w.purchaser),
    enquiry_type: toEnquiryType(w.enquiry_type),
    value: money(w.total, baseCurrency),
    blocker: null,
    quotation_id: null,
    vessel: w.vessel ? str(w.vessel) : null,
  }
}

export function useEnquiries(query: EnquiryQuery = {}, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: qk.enquiries(query),
    // The board polls as a stand-in for the `enquiry.status` event; other callers don't.
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const page = await callGet<WireList<WireEnquirySummary>>('enquiry', 'get_enquiries', {
        // `status`/`purchaser`/`sales_person` are NOT in the live-verified query shape (only
        // `limit`/`offset` were confirmed) — sent optimistically, carrying forward the filter
        // contract `EnquiryQuery` already had before this task. Flagged in task-7-report.md:
        // needs live confirmation. The mock honours all three.
        status: query.status,
        purchaser: query.purchaser,
        sales_person: query.sales_person,
        limit: query.limit ?? 50,
        offset: query.offset ?? 0,
      })
      let data = page.data.map((row) => toEnquirySummary(row))
      // `awaiting_gt_days` — see the module header: honest now, but filters the returned PAGE.
      if (query.awaiting_gt_days !== undefined) {
        const cutoff = query.awaiting_gt_days
        data = data.filter((e) => e.age_days > cutoff)
      }
      return {
        data,
        total: num(page.total, data.length),
        limit: num(page.limit, query.limit ?? 50),
        offset: num(page.offset, query.offset ?? 0),
      }
    },
  })
}

/* ============================================================================================
 * enquiry.get_enquiry → useEnquiry
 * ============================================================================================ */

export interface WireEnquiryItem {
  name: string
  /** `Enquiry Item.selected_rate` — the contracted price when a blanket order set one. A real
   *  Currency field; `update_enquiry` round-trips it (measured 2026-08-29). */
  selected_rate?: number
  /** The STRING through-key carried from enquiry to RFQ to SQ to comparison to quotation — never
   * coerce this to a number, even though it often looks numeric ("1", "2", …). */
  enquiry_row_no: string
  item_code: string | null
  item_name?: string
  /**
   * The customer's own text for this line, as the mail said it. **The only thing an UNMATCHED
   * line has** — `CreateEnquiryBar` now sends every extracted line, matched or not, so a row with
   * `item_code: null` is normal. Confirmed on the wire live:
   * `raw_text: "Hydrostatic Release Unit, Brand: Hammar, Type: H20, for Life-Raft"`.
   */
  raw_text?: string | null
  /** The customer's own unit — on the wire since 2026-09-15 (`null` until written). See `B11`. */
  customer_uom?: string | null
  item_creation_req_ref?: string | null
  /** Real fields, server-defaulted 1 / 0 — see `EnquiryItem.conversion_factor`. */
  conversion_factor?: number | null
  qty_as_per_stock_uom?: number | null
  description?: string | null
  /** The model's own confidence, 0–1. Really is on the wire — the earlier "no confidence field
   * on this wire (Q8)" note was wrong, and left the CONF. column reading 0% for every line. */
  extraction_confidence?: number | null
  note?: string | null
  qty: number
  uom: string
  route: string
  create_rfq?: number | boolean | string
  suppliers: string[]
}

export interface WireEnquiryDetail {
  name: string
  status: string
  /** The requesting department — a CUSTOM field (`Custom Field` dt=Enquiry), Link to
   *  `Customer Department`. See `toEnquiry`'s note for why this was thought not to exist. */
  custom_customer_department?: string | null
  /** Populated on real enquiries (`AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)`, measured
   *  2026-08-08); `null` on some older rows, which is a real answer and not an absent field. */
  company?: string | null
  /** `'mail'` | `'manual'` — how the enquiry was created. Not itself `EnquiryHeader.type`, but
   * `toEnquiryHeader` derives that field from this one (see the module header). */
  source?: string | null
  customer: string
  customer_name?: string | null
  /** The customer's own reference and its date — edited on the header card since 2026-09-14. */
  customer_reference_no?: string | null
  customer_reference_date?: string | null
  /** What the customer is quoted in; `null` until the header card first saves it. */
  customer_currency?: string | null
  /** The selling price list the delivery location implies; see `EnquiryHeader.price_list`. */
  price_list?: string | null
  sales_person?: string | null
  enquiry_type: string
  purchaser: string
  /** When `assign_purchaser` last ran — present on the wire; no consumer needs it today. */
  assigned_to_purchaser_on?: string | null
  vessel?: string | null
  /** → `EnquiryHeader.delivery_port`. Named `port_of_delivery` on the wire, not `delivery_port`. */
  port_of_delivery?: string | null
  delivery_location?: string | null
  /** → `EnquiryHeader.date`. Named `transaction_date` on the wire, not `date`. */
  transaction_date?: string | null
  /** Frappe `Check`, 0|1 on the wire — see `bool01` in `wire.ts`. */
  use_email_body?: number | boolean
  /** The `Communication` this enquiry was created from — on the wire (measured 2026-08-04,
   * `ENQ-2026-00005`), `null`/absent for a manually-created enquiry or one created before the
   * create-from-mail flow started sending it. See `EnquiryHeader.mail_id`'s own comment. */
  mail_id?: string | null
  age_days: number
  /**
   * When the enquiry was created. On the wire TWICE and identical — measured 2026-08-09,
   * ENQ-2026-00022 returns `creation` and `created_at` both `2026-08-07 13:48:58.578967`. The
   * module header's claim that neither endpoint carries a creation timestamp was true of the LIST
   * payload only. Both are modelled because the method returns both, and preferring `created_at`
   * (the name this API chose) over Frappe's raw `creation` keeps us on the documented one.
   */
  created_at?: string | null
  creation?: string | null
  /** Exists on the wire (measured 2026-08-04) but was wrongly believed absent — see the module
   * header. Not read into `age_days`, which stays server-computed; kept here so the type stops
   * asserting the field doesn't exist. */
  status_changed_at?: string | null
  total?: number
  rfq_count?: number
  items: WireEnquiryItem[]
  file_mapping?: unknown[]
  rfqs?: unknown[]
  /** The AMS-style reference a user reads — present on the wire (often `null` until AMS assigns
   * one, same as `toEnquirySummary`'s row). See `toEnquiry`'s fallback chain below. */
  ref?: string | null
  ams_user_record_no?: string | null
}

/** Exported so `useEnquiryMutations.ts` can NORMALIZE a row before writing it back — see
 * `toWireItemWrite` there (fix round 1, IMPORTANT 3): a row read with `route: ''` and
 * `create_rfq: 1` must not round-trip as `route: '', create_rfq: 0` just because a write only
 * ever looked at `route` verbatim. */
export function toRoute(w: WireEnquiryItem): LineRoute {
  if (w.route === 'contract' || w.route === 'rfq') return w.route
  return w.create_rfq === 1 || w.create_rfq === true || w.create_rfq === '1' ? 'rfq' : 'contract'
}

/**
 * `cust_sno` has no distinct field on this wire (unlike the old `Opportunity Item`, which at
 * least had a documented gap for it — Q10). `enquiry_row_no` is the closest honest proxy: it IS
 * the row's own reference number, carried through the rest of the pipeline — reused here rather
 * than left blank. Flagged in task-7-report.md for confirmation.
 */
export function toEnquiryItem(w: WireEnquiryItem): EnquiryItem {
  const rowNo = str(w.enquiry_row_no) || str(w.name)
  return {
    line_id: rowNo,
    cust_sno: rowNo,
    // The customer's own words FIRST. This used to be `item_name || item_code`, which worked only
    // because every line was guaranteed to have a matched item — the old create path dropped any
    // line it could not resolve. Now that unmatched lines travel, deriving from the item leaves
    // them with NO text at all, and the purchaser has nothing to match against.
    // NEVER `description` — that is the editable field, and showing it here made an edit look
    // like the customer's own words (Akhil, 2026-09-15). A line with no customer text (added by
    // hand) shows the item's name instead.
    raw_text: str(w.raw_text) || str(w.item_name) || str(w.item_code),
    description: str(w.description),
    customer_uom: str(w.customer_uom),
    item_creation_req_ref: w.item_creation_req_ref ? str(w.item_creation_req_ref) : null,
    conversion_factor: num(w.conversion_factor) > 0 ? num(w.conversion_factor) : 1,
    qty_as_per_stock_uom: num(w.qty_as_per_stock_uom),
    item_code: w.item_code ?? null,
    // Really is on the wire. The previous `confidence: 0` (and its "no confidence field — Q8"
    // comment) made the CONF. column read 0% for every line however sure the model was.
    confidence: num(w.extraction_confidence),
    qty: num(w.qty),
    uom: str(w.uom),
    route: toRoute(w),
    contract_id: null, // no per-line contract link on this wire either
    // The contracted price, when a blanket order set one. A real Currency field on the wire.
    selected_rate: num(w.selected_rate),
    suppliers: Array.isArray(w.suppliers) ? w.suppliers.map(String) : [],
    note: str(w.note) || null,
  }
}

function toEnquiryHeader(w: WireEnquiryDetail): EnquiryHeader {
  return {
    // Genuinely absent from the live response — left honestly blank, not guessed. Distinct from a
    // field that IS on the wire but happens to be empty (e.g. `sales_person` below): those carry
    // the server's real value, even when that value is `''`.
    //
    subject: '',
    sender: '',
    company_series: '',
    /*
      ---- `department` EXISTS after all (2026-09-07) ----

      This was mapped to `''` and documented as "no department field — 42 fields, none matching".
      That measurement queried `DocField`, which holds only STANDARD fields; the department is a
      CUSTOM field, and Frappe keeps those in the `Custom Field` doctype where the query never
      looked:

          Custom Field  dt=Enquiry  fieldname=custom_customer_department
                        fieldtype=Link  options=Customer Department

      Confirmed writable end to end on ENQ-2026-00027 — `update_enquiry` with
      `custom_customer_department: "BATTERIES"` reads back through `get_enquiry`. The earlier probe
      also used the wrong NAME (`customer_department`, the stock-ERPNext `Opportunity` field), which
      is why the write appeared to be silently discarded. Ask `E13` can be closed.
    */
    department: str(w.custom_customer_department),
    // `company` IS on the wire and IS populated — measured 2026-08-08, ENQ-2026-00022 and -00018
    // both return `AQUA MARIS SHIPS SUPPLY -(S.P.S - L.L.C)`. It was hardcoded to `''` here and
    // labelled "not on the wire" in the UI, so a real value was shown as blank on every enquiry.
    // `null` on some older rows is a real answer ("no company set"), not an absent field.
    company: str(w.company),
    // Everything below IS on the wire — an empty result here means the SERVER returned it empty
    // (e.g. `sales_person: null` → "not set on this enquiry"), never "the API can't tell us".
    customer: str(w.customer),
    sales_person: str(w.sales_person),
    purchaser: str(w.purchaser),
    enquiry_type: toEnquiryType(w.enquiry_type),
    use_email_body: bool01(w.use_email_body),
    vessel: w.vessel ?? null,
    delivery_location: w.delivery_location ?? null,
    delivery_port: w.port_of_delivery ?? null,
    customer_reference_no: str(w.customer_reference_no),
    customer_reference_date: str(w.customer_reference_date),
    // `null`, not the company currency: a quotation already built from this enquiry was priced
    // in whatever THIS said at the time, and defaulting here would relabel it. The card defaults.
    customer_currency: w.customer_currency ? str(w.customer_currency) : null,
    price_list: w.price_list ? str(w.price_list) : null,
    // No `type` field on the wire, but `source` ('mail' | 'manual' — the same vocabulary
    // `create_enquiry`'s own `source` param takes, see `useEnquiryMutations.ts`) says the same
    // thing under a different name: an enquiry created FROM a mail is the 'Auto' case, one keyed
    // in by hand is 'Manual'. Kept read-only in `EnquiryHeaderCard.tsx` even though it's real,
    // both because it's a derived label (not a field a write can target under this name) and
    // because writing `source` itself was not part of what this fix verified.
    type: w.source === 'mail' ? 'Auto' : 'Manual',
    date: str(w.transaction_date),
    // Empty/`null`/absent all collapse to `null` here — "not sourced from a mail" and "the field
    // was blank" are the same fact for this one, unlike `sales_person` above, which distinguishes
    // a real empty string from an absent field. Nothing downstream needs that distinction for a
    // Link field: `SourcePanel` only ever asks "is there a mail id or not".
    mail_id: w.mail_id ? str(w.mail_id) : null,
  }
}

export function toEnquiry(w: WireEnquiryDetail): Enquiry {
  return {
    id: w.name,
    // Both ARE on the wire (see the module header's CORRECTION note) — same fallback chain
    // `toEnquirySummary` already uses for its own row.
    ref: str(w.ref) || str(w.ams_user_record_no) || w.name,
    status: toBoardStatus(w.status),
    // CORRECTION 2026-08-09: `get_enquiry` DOES carry a creation timestamp — twice, as `creation`
    // and `created_at` (ENQ-2026-00022: both `2026-08-07 13:48:58.578967`). The module header's
    // "no creation timestamp at all" was measured on the LIST payload and wrongly generalised to
    // both, so `ContextBar` read `''` and said "today old" on every flow screen.
    created_at: str(w.created_at) || str(w.creation),
    age_days: num(w.age_days),
    header: toEnquiryHeader(w),
    items: (w.items ?? []).map(toEnquiryItem),
  }
}

/**
 * Polls by default. Status here is a SERVER-DERIVED value (see `polling.ts`) and this one hook
 * backs the status badge on all five detail screens, so a stale read is visible everywhere at
 * once. Deliberately NOT solved with a longer `staleTime` instead — that would just cache a
 * stale derivation for longer.
 *
 * `poll: false` is an opt-out no caller uses today; it exists so a future one need not fork
 * this hook.
 */
export function useEnquiry(id: string | null | undefined, options?: { poll?: boolean }) {
  return useQuery({
    queryKey: qk.enquiry(id ?? ''),
    queryFn: async () => toEnquiry(await callGet<WireEnquiryDetail>('enquiry', 'get_enquiry', { name: id })),
    enabled: !!id,
    refetchInterval: options?.poll === false ? false : POLL_MS,
  })
}

/* ============================================================================================
 * rfq.get_rfqs → useRfqs (Task 8)
 * ============================================================================================ */

/**
 * One line of an RFQ as the SERVER snapshotted it when the RFQ was generated — never derived
 * from live triage, so an edit made after sending can't retroactively rewrite a document a
 * supplier has already been mailed.
 *
 * `enquiry_row_no` is the STRING through-key (never coerce to a number) — carried on the wire but
 * not on `RfqItem` yet (no consumer needs it today); kept on this `Wire*` interface rather than
 * dropped so a future caller can read it without a shape change here.
 */
export interface WireRfqItem {
  item_code: string
  /** The catalogue description. The live wire DOES send this — measured 2026-08-07:
   *  `{"item_code":"9002020005","item_name":"RAYTHEON FU AMPLIFIER 139-152.10 E01",…}`.
   *  Optional only because a row for an item the server cannot resolve may omit it. */
  item_name?: string | null
  enquiry_row_no: string
  qty: number
  uom: string
}

export interface WireRfq {
  name: string
  supplier: string
  supplier_name?: string | null
  status: string
  excel_url?: string | null
  sent_at?: string | null
  /** When this supplier was last chased. Distinct from `sent_at` on purpose: a reminder must not
   *  overwrite the original send time, or the "awaiting N days" the purchaser reads resets to
   *  zero every time they chase — the opposite of what the number is for. Added to the live wire
   *  2026-08-07 alongside `type: 'reminder'` on `send_rfq_email_with_excel`. */
  reminder_sent_at?: string | null
  items: WireRfqItem[]
}
// `req_tag` is on the live response too, but dropped from this interface (review round 1, Minor
// 2): reply-threading actually rides the `[<enquiry-id>] RFQ — …` subject tag (`lib/rfq.ts`'s
// `buildRfqDrafts`), not this field, and nothing here read it. Parsing a field this file never
// consumes invites a future reader to assume it's wired up somewhere.

/**
 * `rfq.get_rfqs`' payload is a **bare array**, not an object with an `rfqs` key.
 *
 * Measured live 2026-08-07 against `rfqdev.etoserp.com`:
 *
 * ```
 * GET rfq.get_rfqs?enquiry=ENQ-2026-00019
 *   {"message":[{"name":"RFQ-2026-00004","supplier":"Shiby",…}]}
 * ```
 *
 * This file previously declared `{rfqs: WireRfq[]}` and read `res.rfqs ?? []`, which against the
 * real server is `undefined ?? []` — **an empty list, every single time.** The RFQ panel therefore
 * never showed a live RFQ, and (worse) `useGenerateRfqs`' "does this supplier already have one?"
 * check read empty too, so it would have raised a duplicate for every supplier and mailed them
 * twice. That second path is now guarded, which is how this was found: the guard fired instead.
 *
 * Exported so the generation path and the display path read ONE definition of the shape. Two
 * independent readers of the same endpoint is exactly how they drifted apart in the first place.
 *
 * Throws rather than defaulting to `[]`: an unreadable list must never be mistaken for "this
 * enquiry has no RFQs", because that is the input the double-send safety trusts.
 */
export function readRfqList(res: unknown, context: string): WireRfq[] {
  if (Array.isArray(res)) return res as WireRfq[]
  // Tolerated because the mock shipped this shape and a server could yet adopt it — but only when
  // the value really is a list. Anything else falls through to the throw.
  const wrapped = (res as { rfqs?: unknown } | null | undefined)?.rfqs
  if (Array.isArray(wrapped)) return wrapped as WireRfq[]
  throw new Error(
    `rfq.get_rfqs did not return a list of RFQs for ${context} — got ${
      res === null || res === undefined ? String(res) : typeof res
    }. Refusing to treat an unreadable response as "no RFQs exist".`,
  )
}

/**
 * `status`'s real vocabulary is unconfirmed beyond the field existing — the live shape this task
 * was given names the field but not its values. Matched case-insensitively against "receiv"
 * rather than a fixed string, so a `Quote Received`/`Received`/`quote_received` spelling all read
 * the same way; anything else (including an unsent `Draft`, which `RfqStatus` has no slot for
 * today) reads as `'sent'` rather than throwing on an unrecognised value.
 */
export function toRfqStatus(value: unknown): RfqStatus {
  const v = typeof value === 'string' ? value.toLowerCase() : ''
  // `Replied` is what the SERVER actually sets once a Supplier Quotation exists against the RFQ —
  // confirmed live 2026-08-07: RFQ-2026-00002, with five quotations, reads `Replied`, while
  // RFQ-2026-00003 with none reads `Sent`. The doctype's full vocabulary is
  // `Draft / Sent / Replied / Expired / Cancelled`.
  //
  // This used to test `includes('receiv')` alone, which "Replied" does not contain — so the one
  // signal the server gives us that a supplier has answered was mapped to `'sent'` and discarded.
  // The Supplier Quotations screen showed "Awaiting" for RFQs the server knew had been replied to.
  return v.includes('receiv') || v.includes('repli') ? 'received' : 'sent'
}

/**
 * Maps one `rfq.get_rfqs` row onto `Rfq`. `email_to` has no source on THIS wire (Q18) — real
 * addresses are resolved separately via `items.get_supplier_emails` (`useItems.ts`'s
 * `useSupplierEmails`), which is what `useSendRfq` (`useEnquiryMutations.ts`) actually sends to.
 * Left honestly blank here rather than guessed.
 *
 * `supplier` is the Link value (`RFQ.supplier`, `reqd`) — identity, not display. Task 8b: this
 * used to prefer `supplier_name` here, which is right for RENDERING but wrong for the RFQ
 * Builder's own "does this supplier already have an RFQ" match (`RfqPage.tsx`, `groupBySupplier`
 * off `EnquiryItem.suppliers[]`, itself now code-keyed) — a display-preferring field silently
 * never matched a code. `supplier_name` carries the trimmed display name separately, for
 * whichever caller wants to render one; the wire's leading-space convention on `supplier_name` is
 * trimmed centrally by `wire.ts`'s `trimmed()` (task 12).
 */
export function toRfq(w: WireRfq): Rfq {
  const items = Array.isArray(w.items) ? w.items : []
  return {
    id: w.name,
    supplier: w.supplier,
    supplier_name: trimmed(w.supplier_name) || undefined,
    item_count: items.length,
    excel_url: str(w.excel_url),
    email_to: '',
    status: toRfqStatus(w.status),
    sent_at: w.sent_at ?? null,
    reminded_at: w.reminder_sent_at ?? null,
    items: items.map((row) => ({
      code: row.item_code,
      // The wire DOES carry `item_name` — this line used to hardcode `''` with a comment claiming
      // otherwise, and a test asserted the blank, so the RFQ template card rendered an empty ITEM
      // NAME column for every row on every screen. Measured on the live bench 2026-08-07.
      // Still falls back to '' rather than the code: a blank cell is honest, a code masquerading
      // as a description is not.
      name: str(row.item_name),
      qty: num(row.qty),
      uom: str(row.uom),
      // The through-key, carried at last — see `RfqItem.enquiry_row_no` for what dropping it cost.
      enquiry_row_no: str(row.enquiry_row_no),
    })),
  }
}

/**
 * RFQs already issued for this enquiry (infra C.4). The RFQ Builder uses it to show the
 * real RFQ number next to a supplier that has one — IDs are the server's to assign, so the
 * split preview never invents one.
 */
export function useRfqs(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.rfqs(id ?? ''),
    queryFn: async () => {
      const res = await callGet<unknown>('rfq', 'get_rfqs', { enquiry: id })
      return readRfqList(res, id ?? '(no enquiry)').map(toRfq)
    },
    enabled: !!id,
  })
}

